import type { RequestOptionsBase } from '@azure/ms-rest-js';
import { useToken } from '@ori/auth';
import type { FC } from 'react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { useApiProviderContext } from '~/contexts/ApiProviderContext';

export interface NotificationsContextValues {
  notificationsCount: number;
  loadNotificationsCount: (
    customerId: number,
    tenant: string,
    options?: RequestOptionsBase,
  ) => Promise<void>;
}

export const NotificationsContext = createContext<NotificationsContextValues | undefined>(
  undefined,
);
NotificationsContext.displayName = 'NotificationsContext';

export const NotificationsContextProvider: FC = ({ children }) => {
  const [notificationsCount, setNotificationsCount] = useState(0);
  const { globalApiClient } = useApiProviderContext();
  const { isAnonymousUser } = useToken();

  const loadNotificationsCount = useCallback(
    async (customerId: number, tenant: string, options?: RequestOptionsBase) => {
      if (!isAnonymousUser) {
        const response = await globalApiClient.getCustomerUnseenNotificationsCount(
          tenant,
          'Web',
          customerId,
          options,
        );

        setNotificationsCount(response.count);
      }
    },
    [globalApiClient, isAnonymousUser],
  );

  const value: NotificationsContextValues = useMemo<NotificationsContextValues>(
    () => ({
      loadNotificationsCount,
      notificationsCount,
    }),
    [loadNotificationsCount, notificationsCount],
  );

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
};

export const useNotificationsContext = () => {
  const context = useContext(NotificationsContext);

  if (context) {
    return context;
  }

  throw new Error('NotificationsContext value must be set.');
};
